import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  X, Loader2, CheckCircle2, Circle, ClipboardCheck, Gem, FileText,
  BarChart3, Target, Puzzle, GraduationCap, CalendarDays, ClipboardList,
} from "lucide-react";

function fmtDate(s) {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" });
  } catch { return s; }
}

function StatusRow({ ok, icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-[#F0ECE4] last:border-0" data-testid={`activity-row-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className={`h-9 w-9 rounded-xl grid place-items-center shrink-0 ${ok ? "bg-[#4A6B53]/12 text-[#4A6B53]" : "bg-[#F0ECE4] text-[#9C9A94]"}`}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-[#1A1C1A] text-sm">{label}</span>
          {ok ? (
            <span className="text-xs px-2 py-0.5 rounded-full bg-[#4A6B53]/12 text-[#4A6B53] inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Sudah</span>
          ) : (
            <span className="text-xs px-2 py-0.5 rounded-full bg-[#F0ECE4] text-[#9C9A94] inline-flex items-center gap-1"><Circle className="h-3 w-3" /> Belum</span>
          )}
        </div>
        {children && <div className="text-xs text-[#5C5F5C] mt-1 space-y-0.5">{children}</div>}
      </div>
    </div>
  );
}

export default function StudentActivityModal({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/users/${userId}/activity`);
        if (alive) setData(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Gagal memuat aktivitas siswa");
        onClose();
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [userId]);

  const u = data?.user;

  const [access, setAccess] = useState({ pro: false, tryout: false });
  const [savingAccess, setSavingAccess] = useState(null);
  useEffect(() => {
    if (data) setAccess({ pro: !!data.asesmen_pro_unlocked, tryout: !!data.free_tryout });
  }, [data]);
  const toggleAccess = async (key) => {
    const next = !access[key];
    setSavingAccess(key);
    try {
      await api.post(`/admin/users/${userId}/access`, { [key]: next });
      setAccess((a) => ({ ...a, [key]: next }));
      toast.success(next ? "Akses gratis diaktifkan" : "Akses gratis dinonaktifkan");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mengubah akses"); }
    setSavingAccess(null);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="activity-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#F0ECE4] sticky top-0 bg-white rounded-t-2xl">
          <div className="min-w-0">
            <h2 className="font-heading font-bold text-lg text-[#1A1C1A] truncate">{u?.name || "Aktivitas Siswa"}</h2>
            <p className="text-xs text-[#9C9A94] truncate">{u?.email} {u?.kelas ? `· Kelas ${u.kelas}` : ""} {u?.jenjang ? `· ${u.jenjang}` : ""}</p>
          </div>
          <button onClick={onClose} data-testid="close-activity-btn" className="p-1 text-[#9C9A94] hover:text-[#1A1C1A] shrink-0"><X className="h-5 w-5" /></button>
        </div>

        {loading ? (
          <div className="p-10 grid place-items-center text-[#9C9A94]"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : data ? (
          <div className="px-6 py-2">
            <p className="text-xs text-[#9C9A94] py-2">Terdaftar sejak {fmtDate(u?.created_at)}</p>

            {data.parent_of && (
              <div className="rounded-xl bg-[#3E6B8A]/10 text-[#3E6B8A] text-xs px-3 py-2 mb-1" data-testid="parent-view-note">
                Menampilkan data anak <b>{u?.name}</b> (dari akun orang tua {data.parent_of}).
              </div>
            )}

            {u?.role === "siswa" && (
              <div className="clay-card p-3 my-2 bg-[#FAF8F5]" data-testid="access-control">
                <p className="text-xs font-semibold text-[#1A1C1A] mb-1">Kontrol Akses Gratis (Admin)</p>
                {[{ k: "pro", label: "Asesmen Pro" }, { k: "tryout", label: "TryOut TalentaPath" }].map(({ k, label }) => (
                  <div key={k} className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-[#1A1C1A]">{label}</span>
                    <button onClick={() => toggleAccess(k)} disabled={savingAccess === k} data-testid={`toggle-access-${k}`}
                      className={`relative h-6 w-11 rounded-full transition-colors ${access[k] ? "bg-[#4A6B53]" : "bg-[#D9D4C9]"} disabled:opacity-60`}>
                      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${access[k] ? "left-[22px]" : "left-0.5"}`} />
                    </button>
                  </div>
                ))}
                <p className="text-[11px] text-[#9C9A94] mt-1">Aktifkan agar siswa ini bisa akses gratis tanpa pembayaran.</p>
              </div>
            )}

            <StatusRow ok={!!data.asesmen} icon={ClipboardCheck} label="Asesmen Potensi Diri">
              {data.asesmen ? (
                <>
                  <p>Dikerjakan {fmtDate(data.asesmen.created_at)}{data.asesmen.variant ? ` · variant ${data.asesmen.variant}` : ""}</p>
                  {data.asesmen.tipe_dominan && <p>Tipe dominan: <b>{data.asesmen.tipe_dominan}</b></p>}
                </>
              ) : <p>Belum mengerjakan asesmen internal.</p>}
            </StatusRow>

            <StatusRow ok={data.asesmen_pro_unlocked} icon={Gem} label="Asesmen Pro">
              <p>{data.asesmen_pro_unlocked ? "Akses Pro sudah terbuka." : "Belum membeli / membuka Asesmen Pro."}</p>
            </StatusRow>

            <StatusRow ok={!!data.iti} icon={FileText} label="Laporan iTi (Talenta)">
              {data.iti ? (
                <>
                  <p>Diupload {fmtDate(data.iti.uploaded_at)}</p>
                  {data.iti.tipe && <p>Tipe kepribadian: <b>{data.iti.tipe}</b></p>}
                  {data.iti.cita_cita_siswa && <p>Cita-cita ditulis siswa: <b>{data.iti.cita_cita_siswa}</b></p>}
                </>
              ) : <p>Belum ada laporan iTi terunggah.</p>}
            </StatusRow>

            <StatusRow ok={data.tryout_internal?.length > 0} icon={BarChart3} label="TryOut TalentaPath">
              {data.tryout_internal?.length > 0 ? (
                <ul className="space-y-0.5">
                  {data.tryout_internal.map((t, i) => (
                    <li key={i}>• <b>{t.nama}</b> — skor {t.skor} ({t.benar}/{t.total}) · {fmtDate(t.created_at)}</li>
                  ))}
                </ul>
              ) : <p>Belum mengerjakan TryOut di aplikasi.</p>}
            </StatusRow>

            <StatusRow ok={data.tryout_partner?.length > 0} icon={GraduationCap} label="TryOut/Simulasi Talenta">
              {data.tryout_partner?.length > 0 ? (
                <ul className="space-y-0.5">
                  {data.tryout_partner.map((t, i) => (
                    <li key={i}>• <b>{t.batch_name}</b> — {t.persen_benar != null ? `${t.persen_benar}% benar` : "-"}{t.total_irt != null ? `, IRT ${t.total_irt}` : ""}{t.kelulusan && t.kelulusan !== "-" ? ` · ${t.kelulusan}` : ""} · {fmtDate(t.uploaded_at)}</li>
                  ))}
                </ul>
              ) : <p>Belum ada hasil TryOut Talenta.</p>}
            </StatusRow>

            <StatusRow ok={data.postes_harian?.count > 0} icon={CalendarDays} label="Postes Harian">
              {data.postes_harian?.count > 0 ? (
                <p>{data.postes_harian.count} sesi postes sudah diupload admin.</p>
              ) : <p>Belum ada sesi postes yang diupload.</p>}
            </StatusRow>

            <StatusRow ok={!!data.recognize_mitra?.done} icon={ClipboardList} label="Recognize / Postes Talenta">
              {data.recognize_mitra?.done ? (
                <p>Hasil postes/recognize dari Talenta sudah diupload.</p>
              ) : <p>Belum ada hasil postes Talenta terunggah.</p>}
            </StatusRow>

            <StatusRow ok={data.susun_langkah_done} icon={Target} label="Susun Langkah">
              {data.susun_langkah_done ? (
                <>
                  {data.susun_langkah.bidang_minat && <p>Bidang minat: <b>{data.susun_langkah.bidang_minat}</b></p>}
                  {data.susun_langkah.mapel_pilihan?.length > 0 && <p>Mapel pilihan: {data.susun_langkah.mapel_pilihan.join(", ")}</p>}
                  {data.susun_langkah.target_pendidikan && (
                    <p>Target: <b>{[data.susun_langkah.target_pendidikan.prodi, data.susun_langkah.target_pendidikan.ptn].filter(Boolean).join(" · ")}</b></p>
                  )}
                  {data.susun_langkah.target_prodi?.length > 0 && (
                    <p>Target prodi: {data.susun_langkah.target_prodi.map((p) => `${p.prodi}${p.ptn ? ` (${p.ptn})` : ""}`).join(", ")}</p>
                  )}
                  {data.susun_langkah.rencana?.length > 0 && <p>{data.susun_langkah.rencana.length} rencana tersimpan.</p>}
                </>
              ) : <p>Belum menyusun langkah / target pendidikan.</p>}
            </StatusRow>

            <StatusRow ok={data.sudoku?.count > 0} icon={Puzzle} label="Latihan Otak (Sudoku)">
              {data.sudoku?.count > 0 ? (
                <p>{data.sudoku.count}x bermain · terakhir {fmtDate(data.sudoku.last?.created_at)} ({data.sudoku.last?.difficulty})</p>
              ) : <p>Belum pernah bermain.</p>}
            </StatusRow>

            <div className="h-3" />
          </div>
        ) : null}
      </div>
    </div>
  );
}
